import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

const InteractiveBlock = ({
  onSecretActivate,
  id,
  style,
  className,
  isShy = false,
  isExcited = false,
  isShocked = false,
  isShaking = false,
  isCheerful = false,
  isPeeking = false,
}) => {
  const containerRef = useRef(null);
  const isShyRef = useRef(isShy);
  const isExcitedRef = useRef(isExcited);
  const isShockedRef = useRef(isShocked);
  const isShakingRef = useRef(isShaking);
  const isCheerfulRef = useRef(isCheerful);
  const isPeekingRef = useRef(isPeeking);

  // Update ref when prop changes so animation loop sees it
  useEffect(() => {
    isShyRef.current = isShy;
  }, [isShy]);

  useEffect(() => {
    isExcitedRef.current = isExcited;
  }, [isExcited]);

  useEffect(() => {
    isShockedRef.current = isShocked;
  }, [isShocked]);

  useEffect(() => {
    isShakingRef.current = isShaking;
  }, [isShaking]);

  useEffect(() => {
    isCheerfulRef.current = isCheerful;
  }, [isCheerful]);

  useEffect(() => {
    isPeekingRef.current = isPeeking;
  }, [isPeeking]);

  // ... (in loop) ...
  // We generally can't easily multi-replace broadly without context,
  // so let's just do the effect log first.
  // Actually, I can use a separate chunk for the loop log.
  // Let's do that.

  useEffect(() => {
    let scene, camera, renderer;
    let shadowLight, backLight, light;
    let block;
    let isBlowing = false;
    let isHolding = false;
    let holdStartTime = 0;
    let holdTimer = null;
    let mousePos = { x: 0, y: 0 };
    let clock = new THREE.Clock();
    let time = 0;
    let deltaTime = 0;
    let raycaster = new THREE.Raycaster();
    let mouse = new THREE.Vector2();

    // Natural blink state variables
    let lastBlinkTime = 0;
    let nextBlinkDelay = 5 + Math.random() * 15; // 5-20 seconds
    let isBlinking = false;
    let blinkStartTime = 0;
    const blinkDuration = 0.2; // 200ms for natural blink
    let blinkCount = 0; // Track blinks in current sequence
    let shouldDoubleBlink = false; // 20% chance flag

    // Helper functions
    const rule3 = (v, vmin, vmax, tmin, tmax) => {
      var nv = Math.max(Math.min(v, vmax), vmin);
      var dv = vmax - vmin;
      var pc = (nv - vmin) / dv;
      var dt = tmax - tmin;
      var tv = tmin + pc * dt;
      return tv;
    };

    // Classes
    class Block {
      constructor() {
        this.threegroup = new THREE.Group();

        // Simple block geometry
        const geometry = new RoundedBoxGeometry(100, 100, 100, 4, 10);
        const material = new THREE.MeshLambertMaterial({
          color: 0xfa5e3c,
          flatShading: true,
        });

        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;

        this.threegroup.add(this.mesh);

        // Eyes
        // const eyeGeom = new THREE.BoxGeometry(20, 20, 10);
        // const eyeGeom = new RoundedBoxGeometry(20, 20, 10, 4, 5);

        const eyeGeom = new THREE.CylinderGeometry(10, 10, 10, 32);
        eyeGeom.rotateX(Math.PI / 2);
        const eyeMat = new THREE.MeshLambertMaterial({
          color: 0xffffff,
          flatShading: true,
        });

        this.leftEye = new THREE.Mesh(eyeGeom, eyeMat);
        // this.leftEye.position.set(-25, 10, 50);
        this.leftEye.position.set(-20, 10, 50);

        this.rightEye = new THREE.Mesh(eyeGeom, eyeMat);
        // this.rightEye.position.set(25, 10, 50);
        this.rightEye.position.set(20, 10, 50);

        this.threegroup.add(this.leftEye);
        this.threegroup.add(this.rightEye);

        // Hands removed

        // Initial position
        this.threegroup.position.y = -50;
        this.threegroup.position.z = 0;
      }

      look(xTarget, yTarget, keepEyesOpen = true) {
        // Rotate block based on mouse position
        const tRotY = rule3(xTarget, -200, 200, -Math.PI / 4, Math.PI / 4);
        const tRotX = rule3(yTarget, -200, 200, -Math.PI / 4, Math.PI / 4);

        this.threegroup.rotation.y +=
          (tRotY - this.threegroup.rotation.y) * 0.1;
        this.threegroup.rotation.x +=
          (tRotX - this.threegroup.rotation.x) * 0.1;

        // Reset eyes only if requested
        if (keepEyesOpen) {
          this.leftEye.scale.y += (1 - this.leftEye.scale.y) * 0.1;
          this.rightEye.scale.y = this.leftEye.scale.y;
        }
      }

      cool(xTarget, yTarget, deltaTime) {
        // React to wind - tilt back and shake slightly
        // Keep looking at mouse (same as look function)
        const tRotY = rule3(xTarget, -200, 200, -Math.PI / 4, Math.PI / 4);
        const tRotX = rule3(yTarget, -200, 200, -Math.PI / 4, Math.PI / 4);

        // Add some wind shake - nodding (rotation X)
        const windShake = Math.sin(time * 20) * 0.05;

        this.threegroup.rotation.y +=
          (tRotY - this.threegroup.rotation.y) * 0.1;
        this.threegroup.rotation.x +=
          (tRotX - this.threegroup.rotation.x) * 0.1 + windShake;

        // Push back slightly
        this.threegroup.position.z += (50 - this.threegroup.position.z) * 0.1;

        // Reset vertical position
        this.threegroup.position.y += (-50 - this.threegroup.position.y) * 0.1;

        // Squint eyes
        this.leftEye.scale.y += (0.1 - this.leftEye.scale.y) * 0.1;
        this.rightEye.scale.y = this.leftEye.scale.y;
      }

      nod(holdProgress, amplitude = null) {
        // Nodding animation based on hold progress (0 to 1)
        // amplitude: optional override for nod movement size (default uses progress-based calculation)
        const baseAmplitude =
          amplitude !== null ? amplitude : 0.1 + holdProgress * 0.2;
        const nodIntensity =
          Math.sin(time * 10 + holdProgress * Math.PI * 2) * baseAmplitude;
        this.threegroup.rotation.x += nodIntensity;

        // Eyes get smaller as progress increases (squinting)
        const eyeScale = 1.0 - holdProgress * 0.5;
        this.leftEye.scale.y = eyeScale;
        this.rightEye.scale.y = eyeScale;
      }

      reset() {
        this.threegroup.position.z += (0 - this.threegroup.position.z) * 0.1;
        this.threegroup.position.y += (-50 - this.threegroup.position.y) * 0.1;
      }
    }

    const init = () => {
      scene = new THREE.Scene();
      const HEIGHT = containerRef.current.clientHeight;
      const WIDTH = containerRef.current.clientWidth;
      const aspectRatio = WIDTH / HEIGHT;
      const fieldOfView = 60;
      const nearPlane = 1;
      const farPlane = 2000;

      camera = new THREE.PerspectiveCamera(
        fieldOfView,
        aspectRatio,
        nearPlane,
        farPlane,
      );
      camera.position.z = 800;
      camera.position.y = 0;
      camera.lookAt(new THREE.Vector3(0, 0, 0));

      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(WIDTH, HEIGHT);
      renderer.shadowMap.enabled = true;

      containerRef.current.appendChild(renderer.domElement);

      window.addEventListener("resize", handleWindowResize, false);
      document.addEventListener("mousemove", handleMouseMove, false);
      document.addEventListener("mousedown", handleMouseDown, false);
      document.addEventListener("mouseup", handleMouseUp, false);
      document.addEventListener("touchstart", handleTouchStart, false);
      document.addEventListener("touchend", handleTouchEnd, false);
      document.addEventListener("touchmove", handleTouchMove, false);
    };

    const handleWindowResize = () => {
      if (!containerRef.current) return;
      const HEIGHT = containerRef.current.clientHeight;
      const WIDTH = containerRef.current.clientWidth;
      // windowHalfX = WIDTH / 2;
      // windowHalfY = HEIGHT / 2;
      renderer.setSize(WIDTH, HEIGHT);
      camera.aspect = WIDTH / HEIGHT;
      camera.updateProjectionMatrix();
    };

    const handleMouseMove = (event) => {
      mousePos = { x: event.clientX, y: event.clientY };
    };

    const handleMouseDown = (event) => {
      // Check if click is within the block container bounds
      if (containerRef.current && block) {
        const rect = containerRef.current.getBoundingClientRect();
        const clickX = event.clientX;
        const clickY = event.clientY;

        // Check if click is within container
        if (
          clickX >= rect.left &&
          clickX <= rect.right &&
          clickY >= rect.top &&
          clickY <= rect.bottom
        ) {
          // Convert mouse position to normalized device coordinates (-1 to +1)
          mouse.x = ((clickX - rect.left) / rect.width) * 2 - 1;
          mouse.y = -((clickY - rect.top) / rect.height) * 2 + 1;

          // Update raycaster with camera and mouse position
          raycaster.setFromCamera(mouse, camera);

          // Get all meshes in the block group
          const intersectObjects = [];
          block.threegroup.traverse((child) => {
            if (child.isMesh) {
              intersectObjects.push(child);
            }
          });

          // Check for intersections
          const intersects = raycaster.intersectObjects(intersectObjects, true);

          // Only activate if we actually clicked on the block
          if (intersects.length > 0) {
            // Start secret hold detection
            isHolding = true;
            holdStartTime = Date.now();

            // Start hold timer - activate secret after 2 seconds
            holdTimer = setTimeout(() => {
              if (isHolding && onSecretActivate) {
                onSecretActivate();
              }
              isHolding = false;
            }, 2000); // Changed from 5000 to 2000 (2 seconds)

            // Regular blowing effect - only if clicked on block
            isBlowing = true;
          }
        }
      }
    };

    const handleMouseUp = () => {
      isBlowing = false;
      // Cancel secret activation if released early
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      isHolding = false;
    };

    const handleTouchStart = (event) => {
      if (event.touches.length > 1) {
        event.preventDefault();
        mousePos = { x: event.touches[0].pageX, y: event.touches[0].pageY };
        isBlowing = true;
      }
    };

    const handleTouchEnd = () => {
      isBlowing = false;
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 1) {
        event.preventDefault();
        mousePos = { x: event.touches[0].pageX, y: event.touches[0].pageY };
        isBlowing = true;
      }
    };

    const createLights = () => {
      // Ambient light (HemisphereLight) - increased intensity
      light = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.9);

      // Main directional light (ShadowLight) - increased intensity
      shadowLight = new THREE.DirectionalLight(0xffffff, 1.2);
      shadowLight.position.set(200, 200, 200);
      shadowLight.castShadow = true;

      // Back light - increased intensity
      backLight = new THREE.DirectionalLight(0xffffff, 0.8);
      backLight.position.set(-100, 200, 50);
      backLight.castShadow = true;

      scene.add(backLight);
      scene.add(light);
      scene.add(shadowLight);
    };

    const createBlock = () => {
      block = new Block();
      scene.add(block.threegroup);
    };

    const loop = () => {
      deltaTime = clock.getDelta();
      time += deltaTime;

      let xTarget = 0;
      let yTarget = 0;

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const containerCenterX = rect.left + rect.width / 2;
        const containerCenterY = rect.top + rect.height / 2;

        xTarget = mousePos.x - containerCenterX;
        yTarget = mousePos.y - containerCenterY;
      }

      if (block) {
        // Apply shaking effect if active (can combine with other states)
        if (isShakingRef.current) {
          const shakeIntensity = 0.03;
          const shakeFreq = 25;
          block.threegroup.rotation.z =
            Math.sin(time * shakeFreq) * shakeIntensity;
          block.threegroup.rotation.x +=
            Math.cos(time * shakeFreq * 0.7) * shakeIntensity * 0.5;
        } else {
          // Smoothly reset shake rotation
          block.threegroup.rotation.z +=
            (0 - block.threegroup.rotation.z) * 0.1;
        }

        // State priority: shocked > cheerful > excited > peeking > shy > blowing > holding > default

        if (isShockedRef.current) {
          // SHOCKED MODE: Wide eyes, look up
          block.look(0, -50, false);
          block.leftEye.scale.y += (1.3 - block.leftEye.scale.y) * 0.15;
          block.rightEye.scale.y = block.leftEye.scale.y;
        } else if (isCheerfulRef.current) {
          // CHEERFUL MODE: Happy wiggle
          const wiggleIntensity = 0.08;
          const wiggleFreq = 12;
          block.threegroup.rotation.z =
            Math.sin(time * wiggleFreq) * wiggleIntensity;
          block.threegroup.rotation.y =
            Math.sin(time * wiggleFreq * 0.5) * wiggleIntensity * 0.5;
          block.look(0, 0, false);
          block.leftEye.scale.y += (1.2 - block.leftEye.scale.y) * 0.15;
          block.rightEye.scale.y = block.leftEye.scale.y;
        } else if (isExcitedRef.current) {
          // EXCITED MODE: Nod (button hover)
          const nodAmplitude = 0.02;
          block.nod(0.5, nodAmplitude);
          block.look(0, 0, false);
          block.leftEye.scale.y = 0.0;
          block.rightEye.scale.y = 0.0;
        } else if (isPeekingRef.current) {
          // PEEKING MODE: Fluid 3-Stage Cycle
          // Total Cycle: ~6.0s
          const cycleDuration = 6.0;
          const phase = time % cycleDuration;

          let targetX = 0;
          let targetY = 0;
          let targetLeftEye = 0.5; // default wide
          let targetRightEye = 0.5;
          let targetRotZ = 0;

          if (phase < 2.0) {
            // PHASE 1: DISINTERESTED (0s - 2.0s)
            // Looking up and right ("Whistling", "Nothing to see")
            // Target: High Up-Right
            targetX = 180;
            targetY = -120;
            // Eyes: Wide open (innocent)
            targetLeftEye = 1.0;
            targetRightEye = 1.0;
            // Head: Tilted back slightly
            targetRotZ = 0.1;
          } else if (phase < 3.5) {
            // PHASE 2: SNEAKY / TRANSITION (2.0s - 3.5s)
            // Slowly turning head towards form but not looking down yet
            // Target: Center-ish, maybe slightly left
            targetX = -50;
            targetY = -20;
            // Eyes: Left eye starts to squint (suspicious side-eye)
            targetLeftEye = 0.4;
            targetRightEye = 0.8;
            // Head: Normalize tilt
            targetRotZ = -0.02;
          } else if (phase < 5.0) {
            // PHASE 3: ACTIVE PEEKING (3.5s - 5.0s)
            // Snaps attention to the form input
            // Target: Down-Left (at inputs)
            targetX = -40;
            targetY = 60;
            // Eyes: Full squint/focus
            targetLeftEye = 0.1;
            targetRightEye = 0.3; // Right eye (further away) opens more to "see" better
            // Head: Leaning in
            targetRotZ = -0.08;
          } else {
            // PHASE 4: RECOVERY (5.0s - 6.0s)
            // Transition back to disinterested
            // Interp targets will handle smooth move back to Phase 1 start
            targetX = 150;
            targetY = -100;
            targetLeftEye = 0.9;
            targetRightEye = 0.9;
            targetRotZ = 0.05;
          }

          // Apply Targets with Smooth Interpolation (Fluidity)
          // We use existing damping logic to smoothly move towards these new setpoints
          // instead of snapping instantly.

          // Re-use logic from look() but manually to control interpolation speed if needed
          // Or just call look() which has built-in 0.1 damping
          block.look(targetX, targetY, false);

          block.leftEye.scale.y +=
            (targetLeftEye - block.leftEye.scale.y) * 0.08; // Slightly smoother eye blink
          block.rightEye.scale.y = block.leftEye.scale.y;
          // If we want asymmetric eyes (squinting one), we set right separately:
          block.rightEye.scale.y +=
            (targetRightEye - block.rightEye.scale.y) * 0.08;

          block.threegroup.rotation.z +=
            (targetRotZ - block.threegroup.rotation.z) * 0.05;
        } else if (isShyRef.current) {
          // SHY MODE: Eyes closed
          block.look(0, 0, false);
          block.leftEye.scale.y += (0.1 - block.leftEye.scale.y) * 0.1;
          block.rightEye.scale.y = block.leftEye.scale.y;
        } else if (isBlowing) {
          block.cool(xTarget, yTarget, deltaTime);
        } else if (isHolding) {
          const holdDuration = Date.now() - holdStartTime;
          const holdProgress = Math.min(holdDuration / 2000, 1.0);
          block.nod(holdProgress);
          block.look(xTarget, yTarget);
        } else {
          // DEFAULT/IDLE STATE - with natural blinking
          block.look(xTarget, yTarget, false); // Don't auto-reset eyes, we control them
          block.reset();

          // Check if it's time to blink
          if (!isBlinking && time - lastBlinkTime > nextBlinkDelay) {
            isBlinking = true;
            blinkStartTime = time;
          }

          // Animate blink
          if (isBlinking) {
            const blinkProgress = (time - blinkStartTime) / blinkDuration;

            if (blinkProgress >= 1) {
              // Blink complete
              blinkCount++;

              // Check for double blink (20% chance, only after first blink)
              if (blinkCount === 1 && !shouldDoubleBlink) {
                shouldDoubleBlink = Math.random() < 0.2;
              }

              if (shouldDoubleBlink && blinkCount < 2) {
                // Do second blink - small pause then restart
                blinkStartTime = time + 0.05; // 50ms pause between blinks
              } else {
                // Blink sequence complete
                isBlinking = false;
                lastBlinkTime = time;
                nextBlinkDelay = 5 + Math.random() * 15; // Random 5-20 seconds for next blink
                blinkCount = 0;
                shouldDoubleBlink = false;
              }
              block.leftEye.scale.y = 1;
              block.rightEye.scale.y = 1;
            } else {
              // Smooth blink using sine curve: 0 -> 1 -> 0
              const blinkAmount = Math.sin(blinkProgress * Math.PI);
              const eyeScale = 1 - blinkAmount * 0.9; // Close to 0.1 at peak
              block.leftEye.scale.y = eyeScale;
              block.rightEye.scale.y = eyeScale;
            }
          } else {
            // Not blinking - keep eyes open
            block.leftEye.scale.y += (1 - block.leftEye.scale.y) * 0.1;
            block.rightEye.scale.y = block.leftEye.scale.y;
          }
        }
      }

      renderer.render(scene, camera);
      requestAnimationFrame(loop);
    };

    init();
    createLights();
    createBlock();
    loop();

    const currentContainer = containerRef.current;

    return () => {
      // Cleanup timer
      if (holdTimer) {
        clearTimeout(holdTimer);
      }

      window.removeEventListener("resize", handleWindowResize);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchmove", handleTouchMove);

      if (currentContainer && renderer.domElement) {
        currentContainer.removeChild(renderer.domElement);
      }

      // Dispose WebGL resources to prevent context loss
      if (renderer) renderer.dispose();

      // Optional: Traverse scene and dispose geometries/materials if needed
      // Since we create new ones each mount, this is good practice
      if (scene) {
        scene.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }
    };
  }, [onSecretActivate]);

  // Allow overriding default fixed styles via props
  const defaultStyle = {
    width: "400px",
    height: "400px",
    position: "fixed",
    top: -70,
    right: -40,
    zIndex: 50,
    pointerEvents: "auto",
  };

  return (
    <div
      id={id || "interactive-block"}
      className={className}
      ref={containerRef}
      style={{
        ...defaultStyle,
        ...style, // allow overrides
      }}
    />
  );
};

export default InteractiveBlock;
